import React, { useEffect, useRef, useState } from 'react';
import {Box, IconButton, Slider, ClickAwayListener, Tooltip, Typography} from '@mui/material';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeMuteIcon from '@mui/icons-material/VolumeMute';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useSessionData } from "../../auth/SessionDataProvider";
import Cookies from 'js-cookie';

const VolumeControl = () => {
    const { sessionData, setSessionData } = useSessionData();
    const volume = sessionData.volume ?? 0.5;

    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const saved = Cookies.get('volume');
        if ((sessionData.volume === undefined || sessionData.volume === null) && saved !== undefined) {
            setSessionData((prev) => ({
                ...prev,
                volume: parseFloat(saved),
            }));
        }
    }, [sessionData.volume, setSessionData]);

    const updateVolume = (newVolume) => {
        Cookies.set('volume', newVolume.toString(), { expires: 365 });
        setSessionData((prev) => ({
            ...prev,
            volume: newVolume,
        }));
    };

    const getVolumeIcon = () => {
        if (volume === 0) return <VolumeOffIcon sx={{ color: '#ff4081' }} />;
        if (volume > 0.7) return <VolumeUpIcon sx={{ color: '#b4a0eb' }} />;
        if (volume > 0.3) return <VolumeDownIcon sx={{ color: '#b4a0eb' }} />;
        return <VolumeMuteIcon sx={{ color: '#b4a0eb' }} />;
    };

    const toggleExpanded = () => setExpanded((prev) => !prev);

    const handleVolumeChange = (_, newValue) => {
        updateVolume(parseFloat(newValue));
    };

    const handleClickAway = () => {
        if (expanded) setExpanded(false);
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    px: 0.5,
                    background: expanded ? 'linear-gradient(to right, #1c1f2a, #2d3142)' : 'transparent',
                    borderRadius: 2,
                    border: expanded ? '2px solid #3e8e93' : 'none',
                    boxShadow: expanded ? '0 0 10px rgba(122, 82, 224, 0.5)' : 'none',
                    transition: 'all 0.3s ease'
                }}
            >
                <Tooltip title={volume === 0 ? "Unmute" : "Adjust Volume"}>
                    <IconButton onClick={toggleExpanded} size="small">
                        {getVolumeIcon()}
                    </IconButton>
                </Tooltip>

                {expanded && (
                    <Box sx={{ ml: 1, mr: 2 }}>
                        <Slider
                            aria-label="Volume"
                            min={0}
                            max={1}
                            step={0.05}
                            value={volume}
                            onChange={handleVolumeChange}
                            sx={{
                                width: 120,
                                color: '#b4a0eb',
                                '& .MuiSlider-thumb': {
                                    backgroundColor: '#fff',
                                    border: '2px solid #7a52e0',
                                    '&:hover': {
                                        boxShadow: '0 0 0 6px rgba(122,82,224,0.2)'
                                    }
                                },
                                '& .MuiSlider-track': {
                                    background: 'linear-gradient(to right, #7a52e0, #b4a0eb)'
                                },
                                '& .MuiSlider-rail': {
                                    backgroundColor: '#444'
                                }
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#ccc',
                                fontFamily: 'Orbitron, sans-serif',
                                textAlign: 'center',
                                mt: -1,
                                display: 'block'
                            }}
                        >
                            Volume
                        </Typography>
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default VolumeControl;
